import {observer} from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import {withAppStores} from "../../../../../AppStores";
import type {UserGroup} from "../../../../../data/models/UserGroup";
import type {AppState} from "../../../../../data/state/AppState";
import type {TransportLayer} from "../../../../../data/TransportLayer";
import {FieldV5} from "../../../details/FieldV5";
import {ClickToEditInputV5} from "../../../input/clicktoedit/ClickToEditInputV5";
import {FieldDataType, XyiconFeature} from "../../../../../generated/api/base";
import {DateFormatter} from "../../../../../utils/format/DateFormatter";
import {baseDistance, FlexCenter} from "../../../styles/styles";
import InfoIcon from "../../../icons/circle-information.svg?react";
import {NodataUserDetailsContentStyled, NoDataStyled} from "../users/UserDetailsContentV5";
import {FieldStyled} from "../../../details/Field.styled";
import {DetailsContentV5HeaderStyled} from "../../../details/DetailsContentV5HeaderStyled";
import {UserGroupUsersV5} from "./UserGroupUsersV5";
interface IUserGroupDetailsContentProps {
	readonly userGroups: UserGroup[];
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

@withAppStores("appState", "transport")
@observer
export class UserGroupDetailsContentV5 extends React.Component<IUserGroupDetailsContentProps> {
	private isNameValid = (value: string) => {
		const {appState, userGroups} = this.props;

		return appState.actions.isUserGroupNameValid(value, userGroups[0]);
	};

	private getErrorMessage = (name: string) => {
		return !name.trim() ? "Name cannot be empty!" : this.isNameValid(name) ? "" : "Name needs to be unique!";
	};

	private onNameChange = (value: string) => {
		if (this.isNameValid(value)) {
			this.props.userGroups[0].name = value;
			this.updateApi();
		}
	};

	private onBlur = () => {
		const userGroup = this.props.userGroups[0];

		if (userGroup) {
			this.updateApi();
		}
	};

	private updateApi() {
		const userGroup = this.props.userGroups[0];

		if (userGroup.id) {
			// we're in edit mode
			this.props.transport.services.feature.update(userGroup.id, XyiconFeature.UserGroup, {
				name: userGroup.name,
				userIDList: userGroup.userIds,
			});
		}
	}

	private get userGroupMemberCount() {
		const {appState, userGroups} = this.props;

		return userGroups[0].userIds.filter((id) => appState.actions.getFeatureItemById(id, XyiconFeature.User)).length;
	}

	public override render() {
		const {userGroups, appState} = this.props;
		const userGroup = userGroups[0];

		return (
			<NodataUserDetailsContentStyled>
				{userGroups.length === 0 && <NoDataStyled>Please select a User Group</NoDataStyled>}
				{userGroups.length === 1 && (
					<>
						<UserGroupDetailsContentStyled>
							<DetailsContentV5HeaderStyled>
								<FlexCenter $gap="10px">
									<InfoIcon />
									<h2>Details</h2>
								</FlexCenter>
							</DetailsContentV5HeaderStyled>
							<div className="header">
								<FieldV5 label="Name">
									<ClickToEditInputV5
										disabled={userGroup.isSystem}
										value={userGroup.name}
										onChange={this.onNameChange}
										onBlur={this.onBlur}
										dataType={FieldDataType.SingleLineText}
										getErrorMessage={this.getErrorMessage}
									/>
								</FieldV5>
								<FieldV5 label="Users">
									<ClickToEditInputV5
										value={this.userGroupMemberCount}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>

								<FieldV5 label="Last Updated">
									<ClickToEditInputV5
										value={DateFormatter.format(userGroup.lastModifiedAt)}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>

								<FieldV5 label="Last Updated By">
									<ClickToEditInputV5
										value={appState.actions.renderName(userGroup.lastModifiedBy)}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>
							</div>
						</UserGroupDetailsContentStyled>

						<UserGroupUsersV5 userGroup={userGroup} />
					</>
				)}
			</NodataUserDetailsContentStyled>
		);
	}
}

export const UserGroupDetailsContentStyled = styled.div`
	${DetailsContentV5HeaderStyled} {
		padding-top: ${baseDistance.md};
		padding-left: ${baseDistance.md};
		border-bottom: none;
		padding-bottom: 2px;
		margin-bottom: 10px;
	}

	${FieldStyled} {
		margin-bottom: ${baseDistance.sm};
	}
`;
